<script lang="ts">
  /**
   * The external principals of one exposed sensitive document, in the order the design fixes:
   * owners, then writers, then alphabetically by email with nulls last (design 3.1, #2190 Q4).
   *
   * The list is capped, so the number that matters is `trueCount` - the distinct external
   * principals on the document - not how many rows are drawn. When those differ the notice says
   * so, because a reviewer who reads "7 people" off a capped list has read the wrong number.
   */
  import { formatNumberWithCommas } from "$lib/utils/number";

  import { externalPrincipalsView, type ExposedSensitiveDocument } from "../exposedSensitiveDocuments";

  let { row }: { row: ExposedSensitiveDocument } = $props();

  const view = $derived(externalPrincipalsView(row));

  const roleOf = (principal: { isOwner: boolean; isWriter: boolean }) =>
    principal.isOwner ? "owner" : principal.isWriter ? "writer" : "reader";
</script>

{#if view.trueCount === 0}
  <p class="text-xs text-gray-500" data-testid="exposed-document-principals-empty">No external principals.</p>
{:else}
  <p class="text-xs text-offblack" data-testid="exposed-document-principals-count">
    <span class="font-semibold tabular-nums">{formatNumberWithCommas(view.trueCount)}</span>
    external {view.trueCount === 1 ? "principal" : "principals"} can open this document.
  </p>

  <ul
    class="max-h-48 divide-y divide-gray-100 overflow-auto rounded border border-gray-200 text-xs"
    data-testid="exposed-document-principals"
  >
    {#each view.shown as principal (principal.principalResourceId)}
      <li
        class="flex items-center justify-between gap-2 px-2 py-1"
        data-testid="exposed-document-principal"
        data-principal-id={principal.principalResourceId}
        data-role={roleOf(principal)}
      >
        <span class="min-w-0 truncate" title={principal.principalEmail ?? principal.principalResourceId}>
          {principal.principalEmail ?? "No email on the principal"}
        </span>
        <span class="shrink-0 text-[10px] uppercase tracking-wide text-gray-500">{roleOf(principal)}</span>
      </li>
    {/each}
  </ul>

  {#if view.truncated}
    <p class="text-[11px] text-amber-800" data-testid="exposed-document-principals-truncation">
      Showing {formatNumberWithCommas(view.shown.length)} of
      <span class="tabular-nums">{formatNumberWithCommas(view.trueCount)}</span> external principals; the list is truncated.
    </p>
  {/if}
{/if}
